// Shared srcset/preload helpers for the marketing route heroes.
//
// Deliberately a .mjs with no React/Vite imports: it is consumed BOTH by the
// browser bundle (Landing/Products) and by scripts/prerender-seo.mjs running in
// plain Node, which cannot import a .js from this CommonJS-defaulted package.
// One module means the <picture> srcset and the <link rel=preload> imagesrcset
// can never drift apart — a mismatch there costs a duplicate hero download.
//
// The variant files are produced by scripts/generate-image-variants.mjs and
// committed to public/images/. WIDTHS must match that script's ladder.

export const WIDTHS = [640, 960, 1280, 1600];

// Every hero is full-bleed, so the image always spans the viewport.
export const HERO_SIZES = '100vw';

/**
 * Builds a width-descriptor srcset for one generated format.
 *
 * @param {string} src - Path to the ORIGINAL image, e.g. '/images/hero-racing.jpg'
 * @param {'avif'|'webp'} ext - Generated format to build the set for
 * @returns {string} e.g. '/images/hero-racing-640.avif 640w, ...'
 */
export function heroSrcSet(src, ext) {
  const base = src.replace(/\.[^.]+$/, '');
  return WIDTHS.map((w) => `${base}-${w}.${ext} ${w}w`).join(', ');
}

// The single rung used by background-image heroes. image-set() negotiates format
// but not width, so these heroes always resolve to this one file.
export const HERO_BG_WIDTH = 1280;

/**
 * Attributes for the route's <link rel="preload" as="image">.
 *
 * Preloads AVIF only, and declares `type` so non-AVIF browsers skip the hint
 * instead of racing to fetch a format their <picture> would then reject. Those
 * browsers simply fall back to normal discovery — a lost preload, not a wasted
 * download. Every browser Lighthouse scores with supports AVIF.
 *
 * `background: true` is REQUIRED for heroes painted via heroImageSet(). Those
 * resolve to a fixed rung, so advertising the full ladder here would make the
 * browser preload the rung that matches the viewport (640 on a phone) while CSS
 * went on to fetch HERO_BG_WIDTH — downloading the hero twice and making the
 * route slower than before it was optimized. A single-candidate srcset keeps
 * the hint and the stylesheet pointed at the same file.
 */
export function heroPreload(src, { background = false } = {}) {
  const imagesrcset = background
    ? `${src.replace(/\.[^.]+$/, '')}-${HERO_BG_WIDTH}.avif ${HERO_BG_WIDTH}w`
    : heroSrcSet(src, 'avif');
  return { imagesrcset, imagesizes: HERO_SIZES, type: 'image/avif' };
}

/**
 * CSS `image-set()` value for heroes painted as a background-image (SubPageHero),
 * where <picture> isn't available. Format negotiation only — no width switching —
 * so it picks one rung sized for the largest common hero viewport.
 *
 * Callers must also set a plain `background-image: url(<original>)` first as the
 * fallback declaration for engines without image-set() support.
 */
export function heroImageSet(src, width = HERO_BG_WIDTH) {
  const base = src.replace(/\.[^.]+$/, '');
  return `image-set(url("${base}-${width}.avif") type("image/avif"), url("${base}-${width}.webp") type("image/webp"))`;
}
